import { createContext, useContext } from "react";

/**
 * Who the orchestrator UI acts as, when that is not the signed-in user.
 *
 * The builder's live preview is the case this exists for. It renders the same
 * HubPreview a customer sees, but inside the dashboard, where every request
 * carries the partner admin's own Keycloak JWT — so the agent ran in the
 * partner org, answered from the partner's memory, and dropped its threads into
 * the partner's /agent history, no matter which customer the preview selector
 * named. `previewTenantId` reaches the widget and workflow hooks because each
 * one takes it explicitly; the orchestrator hooks never had a way to receive it.
 *
 * An empty context means "act as the signed-in user", which is what the
 * dashboard's own /agent route and the real embed both want — the embed already
 * authenticates by partner token through setStaticToken.
 */
export interface AgentIdentity {
  /** Bearer token to send instead of the signed-in user's. */
  token?: string;
  /** Org the sessions belong to. Scopes the react-query cache keys. */
  orgId?: string;
}

export const AgentIdentityContext = createContext<AgentIdentity>({});

export function useAgentIdentity(): AgentIdentity {
  return useContext(AgentIdentityContext);
}

/**
 * Merge an override token onto a request init.
 *
 * Returns the init untouched when there is no override, so the default path
 * keeps whatever Authorization apiFetch would have applied on its own.
 */
export function withAgentAuth(init: RequestInit | undefined, token?: string): RequestInit | undefined {
  if (!token) return init;
  return {
    ...init,
    headers: {
      ...((init?.headers as Record<string, string> | undefined) ?? {}),
      Authorization: `Bearer ${token}`,
    },
  };
}
